import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, User, Menu, X, ChevronDown, ShoppingBag } from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../../context/AuthContext';
import { useRetailCart } from '../../context/RetailCartContext';
import { getCategories } from '../../api/services';
import { cn } from '../../utils/format';

export default function Navbar({ onSearchOpen }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  const { count: cartCount } = useRetailCart();
  const { user, isAdmin } = useAuth();
  const location = useLocation();

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-40 bg-white text-ink shadow-sm">
      <div className="mx-auto flex max-w-8xl items-center justify-between gap-4 px-5 py-4 sm:px-8 lg:px-12">
        {/* Mobile menu btn */}
        <button className="lg:hidden" onClick={() => setMobileOpen((v) => !v)} aria-label="Menú">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        <Logo />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 text-sm lg:flex">
          <NavLink to="/catalogo" className="link-underline">Catálogo</NavLink>
          {categories.filter((c) => !c.parent).slice(0, 4).map((c) => (
            <CategoryNavItem
              key={c._id}
              category={c}
              subcategories={categories.filter((s) => String(s.parent) === String(c._id))}
            />
          ))}
          <NavLink to="/ofertas" className="link-underline font-medium text-brand-500">Ofertas</NavLink>
          <NavLink to="/mayorista" className="link-underline font-medium">Mayorista</NavLink>
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          <button onClick={onSearchOpen} className="grid h-10 w-10 place-items-center rounded-full hover:bg-black/5" aria-label="Buscar">
            <Search size={19} />
          </button>
          <Link to={user ? (isAdmin ? '/admin' : '/cuenta') : '/login'} className="grid h-10 w-10 place-items-center rounded-full hover:bg-black/5" aria-label="Cuenta">
            <User size={19} />
          </Link>
          <Link to="/carrito" className="relative grid h-10 w-10 place-items-center rounded-full hover:bg-black/5" aria-label="Carrito">
            <ShoppingBag size={19} />
            {cartCount > 0 && <Badge>{cartCount}</Badge>}
          </Link>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-black/5 bg-mist-50 lg:hidden dark:bg-ink dark:border-white/10"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              <Link to="/catalogo" className="py-2 font-serif text-lg">Todo el catálogo</Link>
              <Link to="/ofertas" className="py-2 font-medium text-brand-500">Ofertas</Link>
              <Link to="/mayorista" className="py-2 font-medium text-brand-500">Venta Mayorista</Link>
              {categories.filter((c) => !c.parent).map((parent) => (
                <div key={parent._id}>
                  <Link to={`/categoria/${parent.slug}`} className="block py-2 font-medium text-ink/80 dark:text-mist-100/80">
                    {parent.name}
                  </Link>
                  {categories.filter((c) => String(c.parent) === String(parent._id)).map((child) => (
                    <Link key={child._id} to={`/categoria/${child.slug}`} className="block py-1.5 pl-4 text-sm text-ink/60 dark:text-mist-100/60">
                      {child.name}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

/**
 * Categoría del menú: si tiene subcategorías, las despliega al pasar el mouse.
 * El nombre sigue siendo un link a la categoría madre.
 */
function CategoryNavItem({ category, subcategories }) {
  const [open, setOpen] = useState(false);
  const hasSubs = subcategories.length > 0;

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <NavLink to={`/categoria/${category.slug}`} className="link-underline flex items-center gap-1">
        {category.name}
        {hasSubs && <ChevronDown size={14} className={cn('transition', open && 'rotate-180')} />}
      </NavLink>

      {hasSubs && open && (
        // pt-3 mantiene el hover al bajar del título al panel (sin hueco)
        <div className="absolute left-0 top-full z-50 pt-3">
          <div className="min-w-[190px] overflow-hidden rounded-xl bg-white py-2 shadow-soft dark:bg-ink-soft">
            <Link
              to={`/categoria/${category.slug}`}
              className="block px-4 py-2 text-sm font-medium text-ink hover:bg-ink/5 dark:text-mist-100 dark:hover:bg-white/5"
            >
              Ver todo
            </Link>
            <div className="my-1 border-t border-ink/5 dark:border-white/5" />
            {subcategories.map((s) => (
              <Link
                key={s._id}
                to={`/categoria/${s.slug}`}
                className="block px-4 py-2 text-sm text-ink/70 hover:bg-ink/5 hover:text-ink dark:text-mist-100/70 dark:hover:bg-white/5"
              >
                {s.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-brand-500 px-1 text-[10px] font-semibold text-white">
      {children}
    </span>
  );
}
